import { Money } from "@redotech/money";
import { RateLandedCost } from "@redotech/redo-fulfillment-customs-common/rate-landed-cost";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { OutboundRate } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { getLcgInfo, landedCostMajorUnits } from "./lcg-info";

export interface InternationalFees {
  dutiesAndFees: number;
  taxes: number;
  total: number;
}

export interface MerchantInternationalFees extends InternationalFees {
  currency: string;
}

export interface FulfillmentOrderCharges {
  labelQuotedCost: number;
  shippingPaid: number;
  percentageMarkup: string;
  percentageMarkupCost: number;
  fixedMarkup: number;
}

export interface CustomerShippingCharges {
  labelQuotedCost: number;
  percentageMarkupCost: number;
  fixedMarkup: number;
  total: number;
  /** Shared markup rate, or null when fulfillment orders were marked up differently. */
  percentageMarkup: string | null;
}

export interface MarginBreakdown {
  fulfillmentOrdersBreakdown: FulfillmentOrderCharges[];
  /** {@link fulfillmentOrdersBreakdown} summed, for the group-level comparison. */
  customerShipping: CustomerShippingCharges;
  customerInternationalFees: InternationalFees | null;
  merchantInternationalFees: MerchantInternationalFees | null;
  /** Presentment shipping revenue; the authoritative figure margin nets against. */
  customerTotal: number;
  merchantTotal: number;
  totalMargin: number;
}

export function getMarginBreakdown(
  fulfillmentGroup: FulfillmentGroupData,
  /** Coerced: rate options type this `number` but carry stringified rates. */
  selectedRate: number | string,
  rateLandedCost?: RateLandedCost | null,
): MarginBreakdown {
  const fulfillmentOrdersBreakdown: FulfillmentOrderCharges[] =
    fulfillmentGroup.totalShippingCost.fulfillmentOrdersBreakdown?.map(
      (order) => {
        const labelQuotedCost = Number(order.labelCost);
        const percentageMarkup = order.markup?.percentage || "0";
        const percentageMarkupCost =
          (Number(percentageMarkup) / 100) * labelQuotedCost;
        const fixedMarkup = Number(order.markup?.amount || "0");

        const shippingPaid =
          labelQuotedCost + percentageMarkupCost + fixedMarkup;

        return {
          shippingPaid,
          percentageMarkup,
          fixedMarkup,
          labelQuotedCost,
          percentageMarkupCost,
        };
      },
    ) || [];

  const customerShipping = sumFulfillmentOrderCharges(
    fulfillmentOrdersBreakdown,
  );

  const groupLandedCost = fulfillmentGroup.landedCost;
  const customerInternationalFees: InternationalFees | null = groupLandedCost
    ? {
        dutiesAndFees: groupLandedCost.duties + groupLandedCost.fees,
        taxes: groupLandedCost.taxes,
        total:
          groupLandedCost.duties + groupLandedCost.taxes + groupLandedCost.fees,
      }
    : null;

  const lcgInfo = getLcgInfo({ landedCost: rateLandedCost });
  const merchantLandedCost = landedCostMajorUnits(lcgInfo);
  const merchantInternationalFees: MerchantInternationalFees | null =
    lcgInfo.showLandedCost
      ? {
          dutiesAndFees: lcgInfo.duties + lcgInfo.fees,
          taxes: lcgInfo.taxes,
          total: merchantLandedCost,
          currency: lcgInfo.currency,
        }
      : null;

  /**
   * Shipping revenue has the customer's landed cost baked in, so the merchant's
   * quoted landed cost is subtracted too. Mirrors the rate dropdown.
   */
  const customerTotal = Number(fulfillmentGroup.totalShippingCost.amount);
  const merchantTotal = (Number(selectedRate) || 0) + merchantLandedCost;

  return {
    fulfillmentOrdersBreakdown,
    customerShipping,
    customerInternationalFees,
    merchantInternationalFees,
    customerTotal,
    merchantTotal,
    totalMargin: customerTotal - merchantTotal,
  };
}

function sumFulfillmentOrderCharges(
  orders: FulfillmentOrderCharges[],
): CustomerShippingCharges {
  const percentageMarkups = new Set(orders.map((o) => o.percentageMarkup));

  return {
    labelQuotedCost: sumBy(orders, (o) => o.labelQuotedCost),
    percentageMarkupCost: sumBy(orders, (o) => o.percentageMarkupCost),
    fixedMarkup: sumBy(orders, (o) => o.fixedMarkup),
    total: sumBy(orders, (o) => o.shippingPaid),
    // One rate can't stand in for orders marked up at different rates.
    percentageMarkup:
      percentageMarkups.size === 1 ? [...percentageMarkups][0]! : null,
  };
}

function sumBy<T>(items: T[], value: (item: T) => number): number {
  return items.reduce((total, item) => total + value(item), 0);
}

/** The available rate matching the group's selection, if it's still quoted. */
export function findSelectedShipmentRate(
  fulfillmentGroup: FulfillmentGroupData,
): OutboundRate | null {
  const selected = fulfillmentGroup.selectedShippingRate;
  if (!selected) {
    return null;
  }
  return (
    fulfillmentGroup.availableShipmentRates?.rates.find(
      (rate) =>
        rate.carrier === selected.carrier && rate.service === selected.service,
    ) ?? null
  );
}

/**
 * Group-level shipping margin in presentment currency, or null when the rate or
 * its landed cost is quoted in another currency and netting would mix them.
 */
export function getShippingMargin(
  fulfillmentGroup: FulfillmentGroupData,
): Money | null {
  const selectedRate = findSelectedShipmentRate(fulfillmentGroup);
  if (!selectedRate) {
    return null;
  }

  const currency = fulfillmentGroup.totalShippingCost.currency;
  const landedCostCurrency = selectedRate.landedCost?.currency;
  if (selectedRate.currency && selectedRate.currency !== currency) {
    return null;
  }
  if (landedCostCurrency && landedCostCurrency !== currency) {
    return null;
  }

  const { totalMargin } = getMarginBreakdown(
    fulfillmentGroup,
    Number(selectedRate.rate || 0),
    selectedRate.landedCost,
  );

  return { amount: totalMargin, currency };
}
